import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  buildOrganizationSchema,
  websiteSchema,
  productSchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
} from "@/data/seoData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SITE = "VSINDLPARK.COM — VS Agro Infra Enterprises";
const BASE_DESC = "VSINDLPARK.COM — Premium industrial, warehouse & commercial plots at IIE Sigaddi, SIIDCUL / SIIDCUL Sigaddi Growth Centre, Kotdwar, Uttarakhand. 500-10,000 sqmtr plots at Rs.7,500/sqmtr. SIDA approved, 90-year leasehold, Uttarakhand Industrial Policy incentives, near Delhi NCR.";

const pageMeta = {
  home: {
    title: `VSINDLPARK.COM — Industrial Plots at IIE Sigaddi, SIIDCUL / SIIDCUL Kotdwar`,
    description: BASE_DESC,
    keywords: "VSINDLPARK, industrial plots kotdwar, SIIDCUL Kotdwar, SIIDCUL plots, Sigaddi Growth Centre, IIE Sigaddi plots, industrial land uttarakhand, warehouse land uttarakhand, logistics park plot kotdwar, factory land near delhi ncr, SIDA approved plots, VS Agro Infra Enterprises, manufacturing plots kotdwar, industrial plot price per sq meter uttarakhand",
  },
  plots: {
    title: `Industrial & Warehouse Plots for Sale — SIIDCUL / SIIDCUL Kotdwar — VSINDLPARK.COM`,
    description: "Browse available industrial & warehouse plots at IIE Sigaddi, Sigaddi Growth Centre, SIIDCUL / SIIDCUL Kotdwar. Sizes 500 to 10,000 sqmtr at Rs.7,500/sqmtr — a fraction of Delhi NCR prices. SIDA site map approval, flexible payments.",
    keywords: "VSINDLPARK, industrial plots sale kotdwar, SIIDCUL land price, SIIDCUL land price, IIE Sigaddi plot rates, Sigaddi Growth Centre plots, warehouse land for sale uttarakhand, industrial land Uttarakhand buy, factory plot kotdwar, industrial plot price per sq meter uttarakhand",
  },
  gallery: {
    title: `Photo Gallery — VSINDLPARK.COM`,
    description: "View photos of industrial establishments, plots and aerial views at IIE Sigaddi, SIIDCUL / SIIDCUL Sigaddi Growth Centre, Kotdwar, Uttarakhand.",
    keywords: "VSINDLPARK, IIE Sigaddi photos, SIIDCUL Kotdwar images, SIIDCUL Kotdwar images, Sigaddi Growth Centre gallery, industrial estate gallery, factory plots photos uttarakhand",
  },
  schemes: {
    title: `Uttarakhand Industrial Incentives & Government Schemes — VSINDLPARK.COM`,
    description: "SIIDCUL / SIIDCUL schemes, Uttarakhand Industrial Policy 2023 & Logistics Policy 2023 benefits, MSME incentives. Capital subsidy, SGST reimbursement, stamp duty exemption, up to 20% logistics capital subsidy. Updated weekly.",
    keywords: "VSINDLPARK, SIIDCUL schemes, SIIDCUL schemes, uttarakhand industrial policy 2023, uttarakhand logistics policy 2023, SGST reimbursement uttarakhand, MSME capital subsidy uttarakhand, stamp duty exemption industrial land uttarakhand, government incentives industry",
  },
  finance: {
    title: `Bank & Finance Options for Industrial Plots — VSINDLPARK.COM`,
    description: "Financing options for industrial & warehouse plot purchase at IIE Sigaddi. Bank loans, NBFC finance, project loans, mortgage options. SBI, PNB, BOB and more.",
    keywords: "VSINDLPARK, industrial plot loan, SIIDCUL plot finance, SIIDCUL plot finance, bank loan industrial land, NBFC industrial finance uttarakhand, warehouse project loan",
  },
  booking: {
    title: `Book an Industrial Plot — VSINDLPARK.COM`,
    description: "Book your industrial or warehouse plot at IIE Sigaddi, SIIDCUL / SIIDCUL Kotdwar. 10% booking, 15% for allotment, 75% at lease deed. Free consultation.",
    keywords: "VSINDLPARK, book industrial plot kotdwar, IIE Sigaddi booking, SIIDCUL plot reservation, SIIDCUL plot reservation, industrial land booking uttarakhand",
  },
  contact: {
    title: `Contact Us — VSINDLPARK.COM`,
    description: "Contact VS Agro Infra Enterprises for plot enquiries and site visits. Phone: +91-9258959058. Office: IIE Sigaddi, SIIDCUL / SIIDCUL Sigaddi Growth Centre, Kotdwar, Uttarakhand.",
    keywords: "VSINDLPARK, VS Agro Infra Enterprises contact, industrial plot enquiry kotdwar, SIIDCUL plot dealer, SIIDCUL plot dealer, IIE Sigaddi office",
  },
  calculator: {
    title: `Investment Calculator — ROI, EMI & Price Forecast — VSINDLPARK.COM`,
    description: "Calculate ROI, EMI, price appreciation and rental yield for industrial plots at IIE Sigaddi, SIIDCUL / SIIDCUL Kotdwar. Plan your investment smartly.",
    keywords: "VSINDLPARK, industrial plot ROI calculator, plot EMI calculator, land price appreciation, rental yield industrial land, investment calculator kotdwar",
  },
  blog: {
    title: `News, Guides & Updates — VSINDLPARK.COM`,
    description: "Latest news, industry guides, price comparisons, policy changes, and investment guides for industrial & warehouse plots at IIE Sigaddi, SIIDCUL / SIIDCUL Kotdwar, Uttarakhand.",
    keywords: "VSINDLPARK blog, industrial news kotdwar, SIIDCUL updates, SIIDCUL updates, uttarakhand industrial policy news, uttarakhand logistics policy, IIE Sigaddi news, kotdwar vs delhi ncr industrial land",
  },
};

export function PageSEO({ page }) {
  const meta = pageMeta[page] || pageMeta.home;
  const [sameAs, setSameAs] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/settings/social-links`)
      .then((r) => setSameAs(Object.values(r.data || {}).filter(Boolean)))
      .catch(() => {});
  }, []);

  const orgSchema = buildOrganizationSchema(sameAs);
  const breadcrumbSchema = buildBreadcrumbSchema(page);
  const isHome = page === "home";

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:url" content={`https://vsindlpark.in${isHome ? "" : `/${page}`}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <link rel="canonical" href={`https://vsindlpark.in${isHome ? "" : `/${page}`}`} />

      {/* Entity SEO: Organization / RealEstateAgent + Reputation (reviews) */}
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      {/* WebSite entity */}
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      {/* Breadcrumbs */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      {/* AEO/GEO: FAQ + Product offers (home only) */}
      {isHome && (
        <script type="application/ld+json">{JSON.stringify(buildFaqSchema())}</script>
      )}
      {isHome && (
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      )}
    </Helmet>
  );
}
